import { Config, Auth } from 'olympe';
import * as React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { registerDataGridRenderer } from '../../shared/datagrid/DataGridRegistry.jsx';
import BCP from '../../../common/section_bcp/DM_BCP.js';
import { materialCacheManager } from './MaterialCacheManager.js';

/**
 * VENDOR_CELL
 * Render values BCP state
 * - type: string
 */
registerDataGridRenderer({
    name: 'VENDOR_CELL',
    render: (_values, options, object) => {
        const placeholder = (options && options.placeholder !== undefined) ? options.placeholder : 'NO VALUE';
        const type = (options && options.type !== undefined) ? options.type : 'mainVendor';
        return (<VendorsRenderer bcp={object} placeholder={placeholder} type={type} />);
    }
});



/**
 * @param {string} payload
 * @return {!Promise<!Object>}
 */
export async function callProxy(payload) {
    const proxyUrl = Config.getParameter('http_proxy_path');
    const rootUrl = Config.getParameter('risk_crisis_path');
    const access_token = sessionStorage.getItem("ms_auth_token");
    console.log("::: vendor renderer: access_token ::::", access_token);

    try {
        // Run request
        const response = await fetch(proxyUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                destination: rootUrl,
                token: Auth.getUserToken().nonce,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${access_token}`,
                },
                opperation: 'POST',
                payload: payload
            }),
        });

        // Convert to json
        const responseObject = await response.json();

        // Handle API error
        return responseObject.statusCode === 500
            ? { statusCode: 1, message: responseObject.message }
            : responseObject;
    }
        // Catch error where httpProxy did not respond
    catch (error) {
        return {
            statusCode: 1,
            message: error.message,
        }
    }
}

/**
 * Render values for vendors
 * @param {!BCP} bcp
 * @param {string} placeholder
 * @param {string} type
 */
const VendorsRenderer = ({ bcp, placeholder, type }) => {
    const [vendors, setVendors] = React.useState([]);

    // Retrieve vendors either from cache or from API
    React.useEffect(() => {
        const pickMainVendors = (materials) => uniqueById(materials.flatMap((material) => material.mainVendor));
        const pickSiteVendors = (materials) => uniqueById(materials.map((material) => {
            return { id: material.vendorSiteNumber, description: material.vendorSiteDescription };
        }));

        // Build id to check cache
        const plant = bcp.get(BCP.plantIdProp);
        const material = bcp.get(BCP.materialIdProp);
        const id = `${plant}.${material};`;

        // Get material from in cache
        const materials = materialCacheManager.getFromCache(id);
        if (materials !== undefined) {
            if (type === 'mainVendor') {
                setVendors(pickMainVendors(materials));
                return;
            }
            setVendors(pickSiteVendors(materials));
        }

        // Get material from API
        else {
            // Build body to fetch vendors from plants & materials
            const body = { offset: '0', limit: '100', plants: [bcp.get(BCP.plantIdProp)], materials: [bcp.get(BCP.materialIdProp)] };

            // Run fetch
            callProxy(body).then((response) => {
                // API error
                if (response.statusCode === 500) {
                    console.error(`API Error: ${response.statusCode} - ${response.message}`);
                    return;
                }

                // Process received materials
                materialCacheManager.setInCache(id, response.materials);
                if (type === 'mainVendor') {
                    setVendors(pickMainVendors(response.materials));
                    return;
                }
                setVendors(pickSiteVendors(response.materials));
            }).catch((error) => {
                console.log(error)
            });
        }
    }, []);

    return (vendors && vendors.length !== 0)
        ? (<List dense={true} sx={{ width: 1 }}>
            {vendors.map((vendor, index) => {
                return (
                    <ListItem key={index} style={{ paddingTop: '1px' }}>
                        <ListItemText primary={vendor.id} secondary={vendor.description} />
                    </ListItem>
                )
            })}
        </List>)
        : (<Typography variant={'body2'}>{placeholder}</Typography>);
}

/**
 * @param {!Array<*>} list
 * @return {!Array<*>}
 */
export function uniqueById(list) {
    return Array.from(new Map(list.map((item) => [item['id'], item])).values());
}
